import $ from '../utils/net';
import Config from './config';
import { getWalletsBalance, getWalletsBonus, getWalletsFido, getWalletsTotIn, getWalletsTotOut } from "./api/api-utility";
import { BonusNotifierService } from './bonus-notifier';
import { BettingAPI } from './api/betting-api';
import { makeReactive } from '../utils/reactive-store';

// const safe = (fn, or) => {try{return fn()}catch(e){return or}};

export default class User {
    private static _state = makeReactive({
        data: false as UserData | false,
        muToken: null as string | null
    });

    // 2. Manteniamo la stessa API statica di prima. 
    // Il resto della tua app non si accorgerà del cambio, ma ora è reattivo.
    static get data() { 
        return User._state.data; 
    }
    static set data(v) { 
        User._state.data = v; 
    }

    static get muToken() { 
        return User._state.muToken; 
    }
    static set muToken(v) { 
        User._state.muToken = v; 
    }

    /**
     * Invia via sms l'OTP necessario per la verifica dell'utente
     * nella procedura di recupero password
     * @param phone
     * @returns {Promise<void>}
     */
    static async passwordRecoverySendSms(phone) {
        return await (Config.AccountApiClass as BettingAPI).apiPasswordRecoverySendSms(phone);
    }

    /**
     * Verifica la validità del codice OTP inviato per la procedura
     * di recupero della password
     * @param code
     * @returns {Promise<void>}
     */
    static async passwordRecoveryVerifyOTP(code) {
        return await (Config.AccountApiClass as BettingAPI).apiPasswordRecoveryVerifyOTP(code);
    }

    /**
     * Verifica la validità del codice OTP in fase di registrazione
     * @param code
     * @returns {Promise<void>}
     */
    static async signupVerifyOTP(code) {
        return await (Config.AccountApiClass as BettingAPI).apiPasswordRecoveryVerifyOTP(code);
    }

    /**
     * Invia la richiesta per la modifica della password utente
     * se il codice OTP corrisponde
     * @param code
     * @param newpassword
     * @param confirmpassword
     * @returns {Promise<any>}
     */
    static async passwordRecoveryAlterPassword(code, newpassword, confirmpassword) {
        return await (Config.AccountApiClass as BettingAPI).apiPasswordRecoveryAlterPassword(code, newpassword, confirmpassword);
    }

    /**
     * Effettua la richiesta di login della user
     */
    static async login(username, password) {
        try {
            const response = await Config.AccountApiClass.apiLogin(username, password);
            User.data = await this.__formatUserDataFromApiResponse(response);
            User.data.success = true;  //> Retrocompatibilità
            BonusNotifierService.reset();
            BonusNotifierService.updateAndCheck(User.data);
            this.optionalBonusNotification();
            return User.data;
        } catch (e) {
            // Trovare il modo di lanciare un'eccezione fatta bene
            if (e === 'link-sent') {
                throw { error: 'link-sent' };
            }
            throw { error: 'login-wrong-credentials' };
        }
    }

    /**
     * Verifica la validità del codice OTP inviato per la procedura
     * di primo login
     * @param otp
     * @param username
     * @param password
     * @returns
     */
    static async firstLoginVerifyOTP(otp, username, password) {
        try {
            await (Config.AccountApiClass as BettingAPI).apiFirstLoginVerifyOTP(otp);
            // OTP corretto, rieseguo il login
            return await User.login(username, password);
        } catch (e) {
            // L'OTP non è corretto o c'è stato un altro errore
            throw { error: 'login-wrong-otp' };
        }
    }

    /**
     * login tramite magic link
     */
    static async tokenLogin(token: string) {
        try {
            const response = await Config.AccountApiClass.tokenLogin(token);
            User.data = await this.__formatUserDataFromApiResponse(response);
            User.data.success = true;  //> Retrocompatibilità
            BonusNotifierService.reset();
            BonusNotifierService.updateAndCheck(User.data);
            this.optionalBonusNotification();
            return User.data;
        } catch (e) {
            throw { error: 'login-wrong-credentials' };
        }
    }

    /**
     * Effettua la richiesta di logout
     * @returns {Promise<*>|Promise<*>|*}
     */
    static async logout() {
        try {
            const output = await Config.AccountApiClass.apiLogout();
            User.data = false;
            BonusNotifierService.reset();
            return output;
        } catch (exception) {
            throw { error: 'logout-failed', exception };
        }
    }

    /**
     * Effettua una richiesta per verificare lo stato della sessione utente
     * Se la sessione è valida ritorna i dati della user connessa
     * @returns {Promise<boolean>}
     */
    static async isSessionOpen() {// ad apertura pagina controllo se l'utente è già loggato
        try {
            const request = Config.AccountApiClass.getUserStatus() as Promise<UserInfoResponse>;
            const meddledResponse = request.catch(e => {
                /*
                Solo bini vede l'errore
                if (User.data && User.data.username == '915003500')
                    alert("Logout reason: "+e);
                */
                if (e == 'not-logged')
                    return false as false;
                throw e;
            })
            const response = await meddledResponse;
            User.data = response == false ? false : await this.__formatUserDataFromApiResponse(response);
            if (User.data) {
                BonusNotifierService.updateAndCheck(User.data);
            } else {
                BonusNotifierService.reset();
            }
            this.optionalBonusNotification();
        } catch (e) {
            // No.
        }
        return User.data;
    }

    static async websiteSignup(
        username,
        password,
        confirmPassword,
        email,
        phone = null,
        firstname = null,
        lastname = null,
        currency = null,
        country = null,
        gender = null,
        birthday = null,
        city = null,
        cap = null,
        address = null,
        agree_age = null,
        agree_privacy = null,
        agree_marketing = null,
        otp = null
    ) {
        const codaff = parseInt(window.safeLocalStorage.getItem('codaff') || '0') || 0;
        return await Config.AccountApiClass.registerUser(
            codaff,
            username,
            password,
            confirmPassword,
            email,
            phone,
            {
                firstname,
                lastname,
                currency,
                country,
                gender,
                birthday,
                city,
                cap,
                address,
                agree_age,
                agree_privacy,
                agree_marketing
            },
            otp
        );
    }

    /**
     * @throws {string} messggio d'errore 
     */
    static async websiteSignupStart(
        username,
        password,
        confirmPassword,
        email,
        phone = null,
        firstname = null,
        lastname = null,
        currency = null,
        country = null,
        gender = null,
        birthday = null,
        city = null,
        cap = null,
        address = null,
        agree_age = null,
        agree_privacy = null,
        agree_marketing = null
    ) {
        const codaff = parseInt(window.safeLocalStorage.getItem('codaff') || '0') || 0;
        return await (Config.AccountApiClass as BettingAPI).registerUserStart(
            codaff,
            username,
            password,
            confirmPassword,
            email,
            phone,
            {
                firstname,
                lastname,
                currency,
                country,
                gender,
                birthday,
                city,
                cap,
                address,
                agree_age,
                agree_privacy,
                agree_marketing
            }
        );
    }

    static async fetchAffiliateAmounts() {
        /*
        let Output = {
            username: null,
            club: false,
            currency: Config.defaultCurrency,
            balance: null,
            saldo: null,
            fido: null,
            rete: null,
            liquido: null,
            "saldo-netto": null,
            totale: null
        };
        */

        return await $.getJSON(
            Config.softionAPI + '/api_user/userLink?json' + (!!Config.publickey ? '&public=' + Config.publickey : ''),
            (err, data) => {
                if (!!err) {
                    throw err;
                }

                if (User.data != false)
                    User.data.affiliateamounts = data;
            }
        );

        // return Output;
    }

    static async fetchBets(lowlimit, searchStatus, recordPerPage, searchHelper, startDate = null, endDate = null) {
        const statuses = {
            V: 'Winner',
            P: 'Loser',
            M: 'To Pay',
            N: 'Pending',
            C: 'Rejected',
            D: 'User Rejected',
            A: 'Cancelled',
            R: 'Waiting Approval',
            S: 'Completed System'
        };

        const Output = {
            recordperpage: 0,
            totalrecords: 0,
            pagecount: 0,
            financial: {},
            financialtotal: {},
            bets: []
        };

        await $.getJSON(
            Config.softionAPI + '/user/verificaScommesse/' + lowlimit + '?json' + (!!Config.publickey ? '&public=' + Config.publickey : '') + '&' + $.serialize({
                perPage: recordPerPage,
                searchStatus: searchStatus,//Aperte
                searchHelper: searchHelper,//Degli ultimi due giorni
                searchStartDate: startDate,
                searchEndDate: endDate,
                l: Config.lang
            }),
            (err, data) => {
                if (!data.bets) {
                    return;
                }

                let dettaglio;
                Output.recordperpage = data.highlimit;
                Output.totalrecords = data.betcount;
                Output.pagecount = data.pagecount;
                Output.financial = data.financial;
                Output.financialtotal = data.financialtotal;
                Output.bets = data.bets.map(
                    B => ({
                        id: B.idbets,
                        currency: !B.display_currency ? 'EUR' : B.display_currency,
                        time: new Date(B.data_scommessa).getTime(),
                        code: B.id,
                        username: B.username,
                        type: B.tipo,
                        importo_scommesso: parseFloat(B.importo_scommesso),
                        vincita_potenziale: parseFloat(B.prob_vincita),
                        vincita: parseFloat(B.importo_vincita),
                        eventi: B.numero_eventi,
                        presi: B.eventi_presi,
                        dettaglio: dettaglio = JSON.parse(B.dettaglio),
                        tot_odd: Object.values(dettaglio).reduce((c: number, O: any) => c * (parseFloat(O.quota) || parseFloat(O.outcome.odd)), 1),
                        statusCode: B.stato,
                        paid: B.paid != 'N',
                        status: statuses[B.stato] || B.stato,
                        cashout: !!B.cashout,
                        live: B.tipo == 'Live',
                        pending: null,
                        raw: B
                    })
                );
            }
        );

        return Output;
    }

    static async fetchRolloverStates(): Promise<RolloverInfo> {
        const body = await $.promiseGetJSON(Config.softionAPI + "/rollovers/rolloverStates");
        if (body.status != 'success')
            throw body.error;
        return body.data as RolloverInfo;
    }

    /**
     * Serve a verificare che esista una sessione valida per la user senza effettuare richieste http
     * @returns {boolean}
     */
    static isLogged() {
        return !!User.data;
    }

    /**
     * Indica se la user connessa è di tipo player
     * Nuovo Metodo
     */
    static isPlayer() {
        return !User.data ? null : !User.data.club;
    }

    /**
     * Indica se la user connessa è di tipo affiliato
     * Nuovo Metodo
     */
    static isAffiliate() {
        return !User.data ? null : !!User.data.club;
    }

    /**
     * Ritorna il saldo dell'utente connesso
     */
    static balance(): number | null {
        return !User.data ? null : User.data.account.balance;
    }

    /**
     * Ritorna il saldo dell'utente connesso
     */
    static bonus(): number | null {
        return !User.data ? null : User.data.account.bonus;
    }

    /**
     * Ritorna il prelevabile
     */
    static payable(): number {
        const payable = !User.data ? null : User.data.account.payable;
        if (payable === null) // In sviluppo, se non c'è il valore uso il vecchio algoritmo
            return (User.balance() - User.bonus());
        return payable;
    }

    /**
     * Ritorna la username dell'utente connesso
     */
    static username(): string | null {
        return !User.data ? null : User.data.username;
    }

    /**
     * Ritorna la username dell'utente connesso
     */
    static accountId(): number | null {
        return !User.data ? null : User.data.id;
    }

    /**
     * Ritorna la url di stampa da utilizzare per la stampa dei ticket dell'utente connesso
     * @returns {any}
     */
    static printurl() {
        return !User.data ? null : User.data.printurl;
    }

    static affiliateamounts() {
        if (!!User.data && !User.data.affiliateamounts) {
            this.fetchAffiliateAmounts().then(() => { });
        }
        return !User.data ? null : User.data.affiliateamounts;
    }

    static bonusNotificationListeners: { (amount: number): void }[] = [];

    /**
     * Il bonus lo notifichiamo all'apertura del sito tramite una modale. E poi mai più
     */
    static hasEverSeenBonus() {
        // @ts-ignore
        const hasKey = window.safeLocalStorage.getItem('bonus-notification') != null;
        return hasKey;
    }

    static setBonusShown() {
        // @ts-ignore
        window.safeLocalStorage.setItem('bonus-notification', Date.now());
    }

    static addBonusNotificationListener(cb: () => void) {
        this.removeBonusNotificationListener(cb);
        this.bonusNotificationListeners.push(cb);
    }

    static removeBonusNotificationListener(cb: () => void) {
        this.bonusNotificationListeners = this.bonusNotificationListeners.filter(_cb => _cb != cb);
    }

    static optionalBonusNotification() {
        if (this.bonus() > 0 && !this.hasEverSeenBonus()) {
            this.bonusNotificationListeners.forEach(cb => cb(this.bonus()));
            this.setBonusShown();
        }
    }

    static forceBonusNotification(amount: number) {
        if (amount > 0) {
            this.bonusNotificationListeners.forEach(cb => cb(amount));
        }
    }

    /**
     * Funzione ad uso interno
     * Serve a formattare i dati locali dell'utente a partire dal responso API
     * @param response
     * @returns {Promise<{"payments-token": boolean, club: boolean, nome, account: {fido, balance, "saldo-netto": number, bonus, liquido: number}, users: (*|boolean), username: *}>}
     * @private
     */
    static async __formatUserDataFromApiResponse(response: UserInfoResponse): Promise<UserData> {
        let userlist: false | SubUserData[] = false;

        if (response.type !== 'player') {
            try {
                userlist = (await Config.AccountApiClass.Admin.getDirectChildren(1))
                    .map(user => ({
                        idaccounts: user.idstruttura,
                        username: user.company,
                        saldo: getWalletsBalance(user.account, user.preferred_currency),
                        currency: user.preferred_currency,

                        //> Dati nuovi:
                        created: user.created,
                        email: user.documents.email,
                        tot_in: getWalletsTotIn(user.account, user.preferred_currency),
                        tot_out: getWalletsTotOut(user.account, user.preferred_currency),
                    }));
            } catch (e) {
                console.warn(e);
            }
            /*
            Config.AccountApiClass.Admin.getDirectChildren(1)
                .then(users => {
                    userlist = users.map(
                        user => ({
                            idaccounts: user.idstruttura,
                            username: user.company,
                            saldo: getWalletsBalance(user.account, user.preferred_currency),
                            currency: user.preferred_currency,

                            //> Dati nuovi:
                            created: user.created,
                            email: user.documents.email,
                            tot_in: getWalletsTotIn(user.account, user.preferred_currency),
                            tot_out: getWalletsTotOut(user.account, user.preferred_currency),
                        })
                    );
                })
                .catch(e => console.warn(e));
            */
        }

        return {
            //> Dato nuovo
            id: response.idstruttura,

            username: response.company,
            nome: response.username,
            club: response.type !== 'player',

            //> Dato nuovo - indica che si è al di sopra del club
            manager: response.type !== 'player' && response.idaccess === 400,

            account: {
                currency: response.preferred_currency,  //> Dato nuovo
                balance: getWalletsBalance(response.wallets, response.preferred_currency),
                fido: getWalletsFido(response.wallets, response.preferred_currency),
                bonus: getWalletsBonus(response.wallets, response.preferred_currency) + getBonusTotal(response.bonus),
                payable: getPayableTotal(response.bonus),
                liquido: 0.00,
                'saldo-netto': 0.00
            },
            users: userlist,
            'payments-token': false,

            ticketprint_url: response.ticketprint_url,
            notifications: response.notifications,
        } satisfies UserData;
    }
}

export interface UserData {
    //> Dato nuovo
    id: number,

    username: string,
    nome: string,
    club: boolean,
    lang?: string,

    //> Dato nuovo - indica che si è al di sopra del club
    manager: boolean,

    account: {
        currency: string,  //> Dato nuovo
        balance: number,
        fido: number,
        bonus: number,
        payable: number | null,
        liquido: number,
        'saldo-netto': number,
    },
    users: SubUserData[] | false,

    'payments-token': false | string,

    ticketprint_url: string,
    printurl?: string,

    success?: boolean,

    affiliateamounts?: {
        currency: string,
        balance: number,
        saldo: number,
        fido: number,
        liquido: number,
        'saldo-netto': number,
    },

    /**
     * Se l'utente ha le notifiche telegram attive
     */
    notifications: boolean;
}

type SubUserData = {
    idaccounts: number;
    username: string;
    saldo: number;
    currency: string;
    created: string | number;
    email?: string;
    tot_in: number;
    tot_out: number;
};

type BonusEntry = {
    display_saldo: number | string;
    display_payable?: string | number;
    'is-bonus': boolean;
};

export interface UserInfoResponse {
    idstruttura: number,
    idcredenziale: number,
    company: string,
    username: string,
    type: 'affiliate' | 'player',
    idparent?: number,
    path: string,
    path_len: number,
    ticketprint_url?: string,
    idaccess: number,
    tipo_accesso: number,
    preferred_currency: string,
    created?: string,
    token?: string,
    notifications: boolean;
    wallets: {
        idwallet: number,
        idstruttura: number,
        idcredenziale: number,
        wallet_type: 'regular' | 'bonus',
        wallet_subtype?: string,
        currency: string,
        balance: string | number,
        fido?: string | number,
        tot_in?: string | number,
        tot_out?: string | number
    }[],
    bonus?: BonusEntry[] // bonus: null | BonusEntry[]
}

function getBonusTotal(response?: BonusEntry[] | null) {
    if (response == null)
        return 0;
    return response.filter(wallet => wallet['is-bonus']).reduce((total, wallet) => total + parseFloat("" + wallet.display_saldo), 0);
}
function getPayableTotal(response?: BonusEntry[] | null): number | null {
    if (response == null)
        return 0;
    if (!response.some(wallet => wallet.display_payable !== undefined))
        return null;
    return response.filter(wallet => !wallet['is-bonus']).reduce((total, wallet) => total + parseFloat("" + wallet.display_payable), 0);
}



export interface WalletContent {
    wallet_id: number,
    bonus: boolean,
    bonus_type: number,  // BonusCampaign.id
    saldo: number
}
export interface RolloverState {
    wallet_id: number,
    progress: number,
    expire_date: number, // timestamp in secondi
    initial_amount: number,
    tot_in: number,
    tot_out: number,
    awarded: boolean,
    wallet: WalletContent
}

export interface BonusCampaign {
    id: number,
    name: string
}

export interface RolloverInfo {
    rollovers: RolloverState[],
    bonuses: BonusCampaign[],
}